import { useQuery } from '@tanstack/react-query'
import * as R from 'remeda'

import { graphql } from '~/gql'
import { useGraphQLRequest } from '~/hooks/useGraphQLRequest'
import { applyIfExist, assertExists, exists, filterExists } from '~/utils'

import { useBrandAccountSurveysQueryKey } from './useBrandAccountSurveysQueryKey'

const document = graphql(`
  query UseBrandAccountSurveysQuery_Main($brandAccountId: ID!) {
    brandAccount(brandAccountId: $brandAccountId) {
      id
      name
      isSurveyEditor
      surveys {
        id
        title
        brandAccountId
        numberOfTotalAnswers
        brandGroups {
          id
          mappedAt
          brands {
            id
          }
        }
        metadata {
          tamNumber
          surveyStartDate {
            day
            month
            year
          }
        }
      }
    }
  }
`)

export const useBrandAccountSurveysQuery = (
  brandAccountId: string | undefined,
) => {
  const queryKey = useBrandAccountSurveysQueryKey(brandAccountId)
  const graphQLRequest = useGraphQLRequest()
  const enabled = exists(brandAccountId)

  return useQuery({
    queryFn: async () => {
      // brandAccountIdが存在するときのみenabledになるので、ここでは必ずbrandAccountIdが存在する
      assertExists(brandAccountId)

      const result = await graphQLRequest({
        document,
        variables: { brandAccountId },
      })

      return R.pipe(
        result.brandAccount,
        applyIfExist((brandAccount) =>
          R.pipe(
            brandAccount.surveys,
            filterExists,
            R.map((survey) => ({
              ...survey,
              numberOfTotalAnswers: survey.numberOfTotalAnswers ?? undefined,
              brandGroups: R.pipe(
                survey.brandGroups,
                filterExists,
                R.map((brandGroup) => ({
                  ...brandGroup,
                  mappedAt: brandGroup.mappedAt ?? undefined,
                  brands: R.pipe(brandGroup.brands, filterExists),
                })),
              ),
              metadata: applyIfExist(survey.metadata, (metadata) => ({
                tamNumber: metadata.tamNumber ?? undefined,
                surveyStartDate: metadata.surveyStartDate ?? undefined,
              })),
            })),
          ),
        ),
      )
    },
    queryKey,
    enabled,
  })
}

export type BrandAccountSurveysQueryData = QueryResultOf<
  typeof useBrandAccountSurveysQuery
>
type QueryData = ReturnType<typeof useBrandAccountSurveysQuery>['data']

export type Survey = NonNullable<QueryData>[number]
